import { useCallback, useEffect, useRef, useState } from "react";
import { notifyError } from "../utils/notify";
import { useApp } from "../context/AppContext";
import {
  BookedService,
  Product,
  GetTransactionsResponse,
  TransactionResponse,
  EditTransactionLineItemsRequest,
} from "../types/transactions";

type Txn = GetTransactionsResponse | TransactionResponse;
type Variant = "service" | "product";
type LineItem = BookedService | Product;
type QueuedSave = {
  variant: Variant;
  item: LineItem;
  quantity: number;
  transactionId: string | null;
};

/** How long after the last +/- press or keystroke the save fires. */
const DEBOUNCE_MS = 500;

/** Build the single-line edit payload for a booked service or product. */
function buildEditRequest(
  variant: Variant,
  item: LineItem,
  quantity: number,
  transactionId: string,
): EditTransactionLineItemsRequest {
  const line = { id: item.id, line_item_id: item.line_item_id, quantity };
  return {
    transaction_id: transactionId,
    line_items:
      variant === "service"
        ? { booked_services: [line] }
        : { products: [line] },
  };
}

const errorNotification = () =>
  notifyError("Your change wasn't saved. Please try again.", {
    title: "Couldn't update quantity",
  });

/**
 * Owns inline line-item quantity editing for the transaction modal:
 *
 *  - Optimistic: `setQuantity` updates the displayed value immediately.
 *  - Debounced: one save per line item fires 500ms after the last change, so
 *    rapid +/- presses coalesce into a single `editTransactionLineItems` call.
 *  - Ordered: each save carries a per-line sequence number; a response that is
 *    no longer the latest (or whose transaction has been swapped out) is
 *    ignored, so a slow older request can't clobber a newer one.
 *  - Durable: a change still inside the debounce window when the modal closes
 *    or switches transactions is FLUSHED (sent immediately), not dropped.
 *  - Saving state: `isSaving(lineItemId)` stays true until the latest save
 *    settles with nothing newer queued; drives the row spinner + summary
 *    skeletons + disabled Checkout.
 *
 * Keyed by `line_item_id` (unique per row).
 */
export function useDebouncedLineItemQuantity(
  transaction: Txn | null,
  onTransactionUpdated: (updated: Txn) => void,
) {
  const { editTransactionLineItems } = useApp();
  const transactionId = transaction?.transaction.id ?? null;

  // Optimistic quantities keyed by line_item_id (absent => show server value).
  const [pending, setPending] = useState<Record<string, number>>({});
  // Rows with a queued or in-flight save.
  const [savingIds, setSavingIds] = useState<Record<string, boolean>>({});

  const timersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  // Latest not-yet-sent change per line item, for flush-on-close.
  const queuedRef = useRef<Record<string, QueuedSave>>({});
  // Monotonic sequence per line item; only the latest save may apply its result.
  const seqRef = useRef<Record<string, number>>({});

  // Keep the latest transaction/handler/client reachable inside async closures.
  const txnRef = useRef(transaction);
  txnRef.current = transaction;
  const onUpdatedRef = useRef(onTransactionUpdated);
  onUpdatedRef.current = onTransactionUpdated;
  const editRef = useRef(editTransactionLineItems);
  editRef.current = editTransactionLineItems;

  const clearSaving = (lineItemId: string) =>
    setSavingIds((prev) => {
      const { [lineItemId]: _done, ...rest } = prev;
      return rest;
    });

  // Reset optimistic state when the modal switches transactions / mounts, and
  // on teardown flush any still-queued change so it isn't silently lost.
  useEffect(() => {
    setPending({});
    setSavingIds({});
    return () => {
      Object.values(timersRef.current).forEach((t) => clearTimeout(t));
      timersRef.current = {};
      const queued = queuedRef.current;
      queuedRef.current = {};
      Object.entries(queued).forEach(
        ([lineItemId, { variant, item, quantity, transactionId: tid }]) => {
          if (!tid) return;
          // Bump the sequence so any save for this line already in flight sees
          // itself as stale and ignores its (older) response — otherwise it
          // would push pre-flush line_items up to the still-mounted parent.
          seqRef.current[lineItemId] = (seqRef.current[lineItemId] ?? 0) + 1;
          // Fire-and-forget: the hook is unmounting, so no state updates — just
          // make sure the last change reaches the server.
          editRef
            .current(buildEditRequest(variant, item, quantity, tid))
            .catch((error) => {
              console.error("Failed to flush line item quantity:", error);
              errorNotification();
            });
        },
      );
    };
  }, [transactionId]);

  const displayQuantity = useCallback(
    (item: LineItem, serverQuantity: number) =>
      pending[item.line_item_id] ?? serverQuantity,
    [pending],
  );

  const isSaving = useCallback(
    (lineItemId: string) => Boolean(savingIds[lineItemId]),
    [savingIds],
  );

  // True while any row has a queued or in-flight save — i.e. server-derived
  // totals are recalculating. Drives the summary skeletons and disabled Checkout.
  const isAnySaving = Object.keys(savingIds).length > 0;

  const save = useCallback(
    async (variant: Variant, item: LineItem, quantity: number) => {
      const lineItemId = item.line_item_id;
      const txn = txnRef.current;
      if (!txn) {
        clearSaving(lineItemId);
        return;
      }

      const savedTxnId = txn.transaction.id;
      const seq = (seqRef.current[lineItemId] ?? 0) + 1;
      seqRef.current[lineItemId] = seq;
      // Still the save whose result should win? (Not superseded, same txn.)
      const isCurrent = () =>
        seqRef.current[lineItemId] === seq &&
        txnRef.current?.transaction.id === savedTxnId;

      try {
        const response = await editRef.current(
          buildEditRequest(variant, item, quantity, savedTxnId),
        );
        if (!isCurrent()) return;
        if (response?.line_items) {
          onUpdatedRef.current({
            ...(txnRef.current as Txn),
            line_items: response.line_items,
          });
        }
        // Drop the optimistic entry only if it still matches what we saved;
        // a newer edit leaves its own pending value in place.
        setPending((prev) => {
          if (prev[lineItemId] !== quantity) return prev;
          const { [lineItemId]: _saved, ...rest } = prev;
          return rest;
        });
      } catch (error) {
        if (!isCurrent()) return;
        console.error("Failed to save line item quantity:", error);
        errorNotification();
        setPending((prev) => {
          const { [lineItemId]: _reverted, ...rest } = prev;
          return rest;
        });
      } finally {
        // Keep the spinner up if a newer save was queued or is in flight.
        if (isCurrent() && !timersRef.current[lineItemId]) {
          clearSaving(lineItemId);
        }
      }
    },
    [],
  );

  const setQuantity = useCallback(
    (variant: Variant, item: LineItem, next: number) => {
      const lineItemId = item.line_item_id;
      setPending((prev) => ({ ...prev, [lineItemId]: next }));
      setSavingIds((prev) => ({ ...prev, [lineItemId]: true }));
      // Remember the latest un-sent value so it can be flushed on close.
      queuedRef.current[lineItemId] = {
        variant,
        item,
        quantity: next,
        transactionId: txnRef.current?.transaction.id ?? null,
      };

      if (timersRef.current[lineItemId]) {
        clearTimeout(timersRef.current[lineItemId]);
      }
      timersRef.current[lineItemId] = setTimeout(() => {
        delete timersRef.current[lineItemId];
        delete queuedRef.current[lineItemId];
        save(variant, item, next);
      }, DEBOUNCE_MS);
    },
    [save],
  );

  return { displayQuantity, isSaving, isAnySaving, setQuantity };
}
